// The ASSISTED COMPLETION surface: a broker or a Phoenix staff member driving
// somebody else's prequalification, in the same sitting.
//
// ONE surface for BOTH the staff app and the partner portal. They differ only
// in which HTTP client can reach which route and where "Back" goes, so those
// are props; everything a human sees or decides lives here once. Decision 18 is
// full parity — Phoenix staff assist exactly as a broker does, through the same
// form, and there is deliberately no third variant.
//
// The FORM ITSELF IS NOT FORKED. The funnel page is rendered unchanged and
// handed an adapter; it is never handed a token, a draft id or a row version.
//
// THE CERTIFICATION CONTROL IS OWNED HERE AND RENDERED THROUGH A SLOT. It is
// built in this file — the form neither knows nor asks what an attestation is —
// and handed to the funnel page as `finalizeSlot`, which the shared
// FinalizeSection drops in above its submit button. The public funnel passes
// nothing and renders exactly what it always did, so this is NOT the fork
// decision 18 forbids: an `if (assisted)` branch inside the shared component
// would be, because the two behaviours would then live in one body and diverge
// on the first bug fixed for one of them.
//
// It sits at FINALIZE rather than on the bar because of what it claims.
// "I certify these answers" is a statement about answers, and until the last
// step there are none to certify; asking for it up front puts the attestation
// before its own subject. Next to the submit button it is read in the presence
// of the thing it attests to. The slot is anchored by the funnel's own
// `stepKey === "finalize"`, never an index — the buyer has six steps and the
// seller seven, and an index would put it on the wrong seller step.
//
// The choice is still read AT SUBMIT TIME through the adapter, so whatever is
// selected when FINALIZE is pressed is what gets recorded.
//
// DECISION 22's resume disclosure does not render here: PrequalSaveProgressCard
// suppresses itself on `mode === "assisted"`, because the invite behind an
// assisted draft is minted with its email deliberately suppressed and promising
// the applicant a link back would promise something nobody sends. Silence would
// be wrong too, so ASSIST_SAVE_NOTE says what IS true — the work is saved as it
// goes, and the invitation can still be resent if they would rather finish it
// themselves. It renders on the funnel's FIRST STEP rather than on this bar:
// "you can stop and come back" is something the operator needs while they are
// deciding whether to keep going, and a note repeated above every step is one
// nobody reads by the third.
//
// THIS BAR IS DELIBERATELY THIN, and stays that way. It is pinned to the top of
// a form the operator scrolls for six or seven steps, so every row on it is a
// row taken off every screen for the whole session — not a one-time scroll.
// Identity, and the way back. Nothing that is only read once belongs here.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { FALLBACK_TIMEZONE, formatDateInTz } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { BuyerPrequalifyPage } from "./BuyerPrequalifyPage";
import { PrequalBrandProvider } from "./PrequalBrandContext";
import {
  clearFunnelState,
  PREQUAL_BUYER_STORAGE_KEY,
  PREQUAL_SELLER_STORAGE_KEY,
} from "./prequalStorage";
import { SellerPrequalifyPage } from "./SellerPrequalifyPage";
import {
  useAssistedPrequalDraftAdapter,
  type AssistedCertification,
  type AssistedCompletionMode,
  type AssistedDraftLoaded,
  type AssistedDraftTransport,
} from "./useAssistedPrequalDraftAdapter";

// ─────────────────────────────────────────────────────────────────────────────
// Copy. Written for the ASSISTING HUMAN — the applicant may well be looking at
// the same screen, which is the whole mechanism of the same-sitting flow, so
// nothing here says anything about them that they should not read.
// ─────────────────────────────────────────────────────────────────────────────

export const ASSIST_TITLE = "Assisted application";

export const ASSIST_IDENTITY_NOTE =
  "Name and email come from the invitation — that is where the documents go.";

export const ASSIST_MODE_LEGEND = "How was this completed?";

/**
 * The certified option's helper.
 *
 * <b>Names the operator, and shows what today is — but only CLAIMS "today".</b>
 * The server stamps `CertifiedAtUtc` at finalize; this renders the browser's
 * idea of the date in Phoenix time. Those can disagree — across the Phoenix
 * midnight boundary, or for an operator sitting in another timezone — so the
 * sentence asserts "today's date", which is true whatever the clock says, and
 * shows the value parenthetically as a convenience rather than as the claim.
 * A helper that read "Recorded against Jane Smith on 27 July" would be a
 * statement this component is in no position to guarantee.
 *
 * Falls back to "your name" only when the session has not resolved a name —
 * never to a blank, and never to a guess.
 */
function certifiedHelper(operatorName: string | null | undefined, today: string): string {
  const who = operatorName?.trim() ? operatorName.trim() : "your name";
  return `Recorded against ${who}, with today's date (${today}, Phoenix time).`;
}

function modeOptions(
  operatorName: string | null | undefined,
  today: string,
): ReadonlyArray<{ value: AssistedCompletionMode; label: string; helper: string }> {
  return [
    {
      // FIRST, and the default. An unmade choice must record the WEAKER claim:
      // defaulting to the attestation would record that a named person vouched
      // for answers they never read, which is the one thing this control exists
      // to prevent. There is deliberately NO "you must choose" guard either —
      // that would make the safe answer cost the same click as the unsafe one.
      value: "SameSittingClientDevice",
      label: "They filled this in themselves",
      helper: "You handed them the keyboard. Nothing is certified against your name.",
    },
    {
      value: "SameSittingBrokerCertified",
      label: "I filled this in for them, and I certify these answers",
      helper: certifiedHelper(operatorName, today),
    },
  ];
}

export const ASSIST_CLIENT_COPY_LABEL = "Email a copy of the documents to them";
export const ASSIST_CLIENT_COPY_HELPER =
  "On by default. Uncheck it only if you are handing the documents over in person "
  + "— we record who chose that.";

export const ASSIST_LOAD_ERROR =
  "We couldn't open this application. It may have been finished, or it may not be "
  + "yours to work on.";

// ─────────────────────────────────────────────────────────────────────────────

export interface AssistedPrequalSurfaceProps {
  draftId: string;
  transport: AssistedDraftTransport;
  /** Where "Back" goes — the list this was opened from. */
  backTo: string;
  backLabel: string;
  /**
   * Who is driving, for the attestation helper.
   *
   * A PROP because the two callers read two different sessions: the portal has
   * `usePortalAuth().me.fullName`, and the staff app has
   * `useAuth().user.displayName` — whose portal probe deliberately settles at
   * `me = null`, so this surface could never resolve it for itself. Same
   * "props for what differs" rule as `transport` and `backTo`.
   *
   * DISPLAY ONLY. The certifier that gets recorded is resolved server-side from
   * the authenticated session and is not on the wire in any field — otherwise
   * one operator could record that a colleague attested to answers they never
   * saw.
   */
  operatorName?: string | null;
}

export function AssistedPrequalSurface({
  draftId,
  transport,
  backTo,
  backLabel,
  operatorName,
}: AssistedPrequalSurfaceProps) {
  const [loaded, setLoaded] = useState<AssistedDraftLoaded | null>(null);
  const [failed, setFailed] = useState(false);

  // Certification is never a default that attests to anything: the weaker,
  // non-attesting mode is preselected and the certified one is an affirmative
  // act. The client copy is ON, so doing nothing SENDS.
  const [completionMode, setCompletionMode] =
    useState<AssistedCompletionMode>("SameSittingClientDevice");
  const [emailClientCopy, setEmailClientCopy] = useState(true);

  const certification = useMemo<AssistedCertification>(
    // Polarity: the checkbox says "email a copy", the column says "suppressed".
    () => ({ completionMode, suppressClientCopy: !emailClientCopy }),
    [completionMode, emailClientCopy],
  );

  const onLoaded = useCallback((dto: AssistedDraftLoaded) => {
    setLoaded(dto);
    setFailed(false);
  }, []);

  // Clear this tab's funnel sessionStorage BEFORE the form mounts.
  //
  // The funnel deliberately prefers its LOCAL blob over the server copy — on
  // the public funnel the local one is never older and can easily be newer
  // (answers typed while the network was down live nowhere else). In assisted
  // mode that rule is exactly wrong: the blob belongs to whoever last used this
  // browser — a previous client, or the broker's own test run on the public
  // funnel — and the server draft is the only thing that belongs to the person
  // sitting here. Left alone, the form would open showing a stranger's answers.
  //
  // Cleared per assisted open rather than per role, so moving from one client
  // to the next in the same tab cannot bleed either. The form re-populates it
  // as it goes, which is what the result screen reads afterwards.
  useEffect(() => {
    clearFunnelState(PREQUAL_BUYER_STORAGE_KEY);
    clearFunnelState(PREQUAL_SELLER_STORAGE_KEY);
  }, [draftId]);

  const adapter = useAssistedPrequalDraftAdapter({
    draftId,
    // The role is not known until the draft loads, so the adapter is built with
    // whichever page ends up rendering it — see the guard below, which is what
    // makes that safe.
    role: loaded?.role ?? "Buyer",
    transport,
    certification,
    onLoaded,
  });

  // This surface reads the draft ITSELF rather than waiting for the form's own
  // mount read, because it cannot choose between the buyer and the seller page
  // without knowing the role. That is two GETs on open, on a console a human
  // opens by hand — the alternative is a page that renders the wrong funnel for
  // a moment, or a form that is handed state it is supposed to fetch.
  useEffect(() => {
    let alive = true;
    void transport
      .load(draftId)
      .then((dto) => {
        if (alive) onLoaded(dto);
      })
      .catch(() => {
        // One message for every failure. The server answers a denial and a
        // missing draft identically on purpose, and inventing a difference here
        // would put the existence oracle back client-side.
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [draftId, transport, onLoaded]);

  if (failed) {
    return (
      <div className="mx-auto max-w-2xl px-space-4 py-space-6">
        <p className="m-0 text-body text-ink" role="alert" data-testid="assist-load-error">
          {ASSIST_LOAD_ERROR}
        </p>
        <div className="mt-space-3">
          <Link to={backTo}>
            <Button type="button" variant="secondary" size="sm">
              {backLabel}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (loaded === null) {
    return (
      <div className="mx-auto max-w-2xl px-space-4 py-space-6">
        <p className="m-0 text-body text-ink-mid" data-testid="assist-loading">
          Opening this application…
        </p>
      </div>
    );
  }

  const FunnelPage = loaded.role === "Seller" ? SellerPrequalifyPage : BuyerPrequalifyPage;

  return (
    <>
      <AssistStrip loaded={loaded} backTo={backTo} backLabel={backLabel} />
      {/* WHOSE application this is. The site was chosen when the draft was
          started, stamped on the row server-side, and comes back on the load
          above — the assist route is addressed by draft id and has no slug for
          the funnel to read, which is why every assisted application rendered
          generic Phoenix before this. `?? null` is deliberate: null means "no
          site" (a Phoenix-run application, correct), while undefined would mean
          "no source given" and send the resolver back to a route param that
          does not exist here.

          The provider is INSIDE the `loaded === null` guard above, so it mounts
          already knowing the site. That is what keeps the funnel's FIRST FRAME
          correct: there is no render in which the form is on screen and the
          resolver still thinks this is a Phoenix application.

          It wraps the funnel page ONLY — never the strip above it. The provider
          renders no DOM, so this is not what keeps the strip un-themed (the
          theme WRAPPER lives inside the funnel page, below), but the nesting
          should still say what is true. */}
      <PrequalBrandProvider source={loaded.partnerSlug ?? null}>
        {/* The funnel page, UNCHANGED in every respect that matters: it receives
            the adapter and a slot, and still never a token, a draft id or a row
            version. The slot is inert on the public funnel, which passes none. */}
        <FunnelPage
          adapter={adapter}
          finalizeSlot={
            <AssistCertification
              operatorName={operatorName}
              completionMode={completionMode}
              onCompletionModeChange={setCompletionMode}
              emailClientCopy={emailClientCopy}
              onEmailClientCopyChange={setEmailClientCopy}
            />
          }
        />
      </PrequalBrandProvider>
    </>
  );
}

interface AssistStripProps {
  loaded: AssistedDraftLoaded;
  backTo: string;
  backLabel: string;
}

function AssistStrip({ loaded, backTo, backLabel }: AssistStripProps) {
  // Sticky rather than fixed: the operator needs the way out and the name of
  // whose form this is from wherever they have scrolled to, and a fixed bar at
  // the bottom of the viewport is the one that ends up covering submit.
  return (
    <div
      className="sticky top-0 z-30 border-b border-line bg-white-pure shadow-[0_2px_12px_rgba(20,20,20,0.06)]"
      data-testid="assist-strip"
    >
      <div className="mx-auto max-w-6xl px-space-4 py-space-2 flex flex-col gap-space-3">
        {/* ONE ROW: who this is for, and out. No step counter — the funnel's own
            chips already say where the operator is, and a second copy on a bar
            that only re-reads the draft on mount could only ever disagree with
            them. */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-space-3 gap-y-space-1">
          <p className="m-0 min-w-0 flex flex-wrap items-baseline gap-x-space-2 gap-y-space-1">
            <span className="text-label uppercase tracking-widest text-gold-deep">
              {ASSIST_TITLE}
            </span>
            <span className="text-body font-semibold text-ink">
              {loaded.firstName} {loaded.lastName}
            </span>
            <span className="text-caption text-ink-mid">
              {loaded.email} · {loaded.role}
            </span>
          </p>
          <Link to={backTo} className="shrink-0">
            <Button type="button" variant="secondary" size="sm">
              {backLabel}
            </Button>
          </Link>
        </div>

      </div>
    </div>
  );
}

interface AssistCertificationProps {
  operatorName?: string | null;
  completionMode: AssistedCompletionMode;
  onCompletionModeChange: (mode: AssistedCompletionMode) => void;
  emailClientCopy: boolean;
  onEmailClientCopyChange: (next: boolean) => void;
}

/**
 * What the operator answers before pressing FINALIZE.
 *
 * Rendered THROUGH the shared form's `beforeSubmit` slot rather than inside it:
 * this component is built here, the form only places it. See the note at the
 * top of this file for why that distinction is the whole of decision 18.
 *
 * Bordered and labelled because it is the one block on the finalize step that
 * is addressed to the OPERATOR — everything above it is the applicant's own
 * contact details, and running the two together invites the wrong person to
 * answer.
 */
function AssistCertification({
  operatorName,
  completionMode,
  onCompletionModeChange,
  emailClientCopy,
  onEmailClientCopyChange,
}: AssistCertificationProps) {
  // Read at RENDER, not captured at mount: an application finished either side
  // of midnight should not show the date the operator opened it.
  const today = formatDateInTz(new Date(), FALLBACK_TIMEZONE);
  const options = modeOptions(operatorName, today);

  return (
    <div
      className="rounded-md border border-line bg-cream/60 p-space-3 flex flex-col gap-space-3"
      data-testid="assist-certification"
    >
      <fieldset className="m-0 border-0 p-0">
        <legend className="p-0 text-caption font-semibold text-ink">
          {ASSIST_MODE_LEGEND}
        </legend>
        <div className="mt-space-2 flex flex-col gap-space-2">
          {options.map((option) => (
            <label
              key={option.value}
              className={cn(
                "flex items-start gap-space-2 text-caption text-ink cursor-pointer",
              )}
            >
              <input
                type="radio"
                name="assist-completion-mode"
                className="mt-space-1 accent-gold"
                value={option.value}
                checked={completionMode === option.value}
                onChange={() => onCompletionModeChange(option.value)}
                data-testid={`assist-mode-${option.value}`}
              />
              <span>
                <span className="font-semibold">{option.label}</span>
                <span className="block text-ink-faint">{option.helper}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex items-start gap-space-2 text-caption text-ink cursor-pointer">
        <input
          type="checkbox"
          className="mt-space-1 accent-gold"
          checked={emailClientCopy}
          onChange={(e) => onEmailClientCopyChange(e.target.checked)}
          data-testid="assist-email-client-copy"
        />
        <span>
          <span className="font-semibold">{ASSIST_CLIENT_COPY_LABEL}</span>
          <span className="block text-ink-faint">{ASSIST_CLIENT_COPY_HELPER}</span>
          {/* Beside the checkbox, because it answers the question the checkbox
              raises: emailed to WHOM. */}
          <span className="block text-ink-faint">{ASSIST_IDENTITY_NOTE}</span>
        </span>
      </label>
    </div>
  );
}
